// @ts-check

/**
 * Extracts the component name from the file name.
 *
 * @param {string} fileName
 * @returns {string}
 *
 * @example
 * extractComponentName("SpButton.ce.vue") // "SpButton"
 * extractComponentName("SpTextField.ce.vue") // "SpTextField"
 */
export function extractComponentName(fileName) {
  return fileName.replace(".ce.vue", "");
}

/**
 * Extracts the component file name from a catalog glob path.
 *
 * @param {string} path - e.g. "../components/SpButton/SpButton.ce.vue"
 * @returns {string}
 */
export function componentFileNameFromPath(path) {
  const segments = path.split("/");
  return segments[segments.length - 1];
}

/**
 * Extracts the app directory name from a catalog glob path.
 *
 * @param {string} path - e.g. "../apps/SpFilterApp/main.js"
 * @returns {string}
 */
export function appDirNameFromPath(path) {
  const segments = path.split("/");
  return segments[segments.length - 2];
}

/**
 * Whether a component opts into form association by declaring `formAssociated: true`.
 *
 * @param {import("vue").DefineComponent} component - The component options object.
 * @returns {boolean}
 */
export function isFormAssociated(component) {
  return Boolean(component?.formAssociated);
}

/**
 * Declares a custom element host as form-associated. Pass to `defineOptions()`
 * inside the `<script setup>` of any host that mounts a form composable
 * (`useForm` / `useFormControl`) — either directly, or through a nested
 * `.ce.vue` piece that has no host element of its own and therefore attaches its
 * `ElementInternals` to this host. Registration reads this flag (via
 * {@link isFormAssociated}) to make the element a form-associated custom element.
 *
 * ```js
 * defineOptions(formAssociatedHost());
 * ```
 *
 * Prefer this over a bare `formAssociated: true`: it names the intent, is
 * greppable, and keeps the `formAssociated` key in one place. The
 * form-association contract test (`framework/form-association.spec.js`) enforces
 * that every host whose boundary uses a form composable declares it.
 *
 * @param {Record<string, unknown>} [options] - Extra component options to merge.
 *   `formAssociated` always wins, so a host cannot accidentally opt back out.
 * @returns {{ formAssociated: true } & Record<string, unknown>}
 */
export function formAssociatedHost(options = {}) {
  return { ...options, formAssociated: true };
}

/**
 * Enriches the component props with the props passed to the custom element.
 *
 * @param {import("vue").DefineComponent} component
 * @param {string} fileName
 * @param {import("vue").ComponentObjectPropsOptions} props
 * @returns {import("vue").ComponentObjectPropsOptions}
 */
export function enrichProps(component, fileName, props) {
  const componentName = extractComponentName(fileName);
  const defaultGlobalProps = props.global ?? {};
  const defaultProps = props[componentName] ?? defaultGlobalProps;

  /**
   * @param {import("vue").ComponentObjectPropsOptions} acc
   * @param {[string, import("vue").Prop<unknown>]} entry
   * @returns {import("vue").ComponentObjectPropsOptions}
   */
  const reducer = (acc, [key, value]) => {
    const defaultPropsValue = props[key] ?? defaultProps[key];

    if (defaultPropsValue !== undefined) {
      value = {
        ...value,
        required: false,
        default: defaultPropsValue,
      };
    }

    return { ...acc, [key]: value };
  };

  return Object.entries(component.props ?? {}).reduce(reducer, {});
}
